import { makeStyles } from "@material-ui/core";
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_PAGE_BG,
} from "./pl-by-program/plByProgramTheme";
import SummaryTab from "./funding-expenditure/Summary";
import DetailsTab from "./funding-expenditure/Details";
import { downloadFundingExpenditureDetailsAPI } from "../../../api/reports-api";
import Loading from "../../../common-components/Loading";

import {
  TH_BG,
  TH_BG_BAND,
} from "../reportTableConfig";

const TAB_ACTIVE = "#4F46E5";
const BORDER = "#E5E7EB";

const TABS = [
  { key: "summary", label: "Summary" },
  { key: "details", label: "Details" },
];

/* Matches the "Filters on all pages" set on the actual Power BI report
   (BU, Year, MonthName, Contract ID, Project ID, Business Type).
   Module-level so it's a stable reference across renders — a fresh array
   literal here would re-trigger every effect/callback keyed on it each render. */
const VISIBLE_FILTER_KEYS = [
  "year",
  "monthName",
  "contractId",
  "projectId",
  "businessType",
  "bu",
];

function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = vKeys
    ? vKeys.filter((k) => k !== "year")
    : FILTERS_CONFIG.filter((f) => f.key !== "year").map((f) => f.key);
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === String(new Date().getFullYear())
  );
}

/* ── Horizontal filter bar (matches PL-by-Program / Labor Utilization's filter popup) ── */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
  activeTab,
}) {
  const filters = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;

  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };

  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];
        /* Details tab's Project ID filter maps to CLIN in that tab's data */
        const label = f.key === "projectId" && activeTab === "details" ? "CLIN" : f.label;

        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{label}</span>

            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}

      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>

      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const FONT = REPORT_FONT;

const useStyles = makeStyles(() => ({
  /* ── layout ── */
  page: {
    fontFamily: FONT,
    backgroundColor: PL_PAGE_BG,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    background: "none",
    transition: "color 0.15s, box-shadow 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 600,
    boxShadow: `inset 0 -2px 0 0 ${TAB_ACTIVE}`,
  },
  headerContainer: { margin: "20px 0 0", padding: "0 16px" },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: { flex: 1, padding: "0 16px", overflowY: "auto", minWidth: 0 },

  /* ── horizontal filter bar (matches P&L by Program / Labor Utilization) ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },

  /* ── summary table ── */
  tableCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "hidden",
    marginBottom: 20,
  },
  tableSectionHead: {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #E2E8F0",
  },
  tableSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.01em",
    fontFamily: FONT,
    margin: 0,
  },
  tableWrap: { overflowX: "auto" },
  table: {
    borderCollapse: "collapse",
    borderSpacing: 0,
    fontFamily: FONT,
    fontSize: 12,
    tableLayout: "fixed",
  },

  /* header */
  thYear: {
    background: TH_BG_BAND,
    color: "#fff",
    padding: "8px 14px",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "left",
    border: "none",
    whiteSpace: "nowrap",
    minWidth: 130,
    position: "sticky",
    left: 0,
    zIndex: 5,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
    boxSizing: "border-box",
  },
  th2026: {
    background: TH_BG_BAND,
    color: "#fff",
    padding: "8px 14px",
    fontWeight: 700,
    textAlign: "center",
    border: "none",
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  thSub: {
    background: TH_BG,
    color: "rgba(255,255,255,.75)",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.06em",
    textAlign: "right",
    whiteSpace: "nowrap",
    border: "none",
    minWidth: 110,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
  },
  thSubLabel: {
    background: TH_BG,
    color: "rgba(255,255,255,.7)",
    padding: "7px 14px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    border: "none",
    position: "sticky",
    left: 0,
    zIndex: 4,
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,.12)",
    boxSizing: "border-box",
  },

  /* data cells */
  tdLabel: {
    padding: "7px 14px",
    fontWeight: 600,
    color: "#111827",
    background: "#F8FAFC",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    position: "sticky",
    left: 0,
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
    boxSizing: "border-box",
  },
  tdVal: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontVariantNumeric: "tabular-nums",
  },
  tdPct: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#4F46E5",
    fontWeight: 600,
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
  },

  /* chart */
  chartCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    padding: "16px 20px",
    marginBottom: 20,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    marginBottom: 8,
    letterSpacing: "-0.01em",
  },

  /* ── details table ── */
  dtCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "hidden",
    marginBottom: 20,
  },
  dtWrap: { overflowX: "auto" },
  dtTable: {
    borderCollapse: "collapse",
    borderSpacing: 0,
    fontFamily: FONT,
    fontSize: 12,
    tableLayout: "fixed",
  },

  /* details headers */
  dtThId: {
    background: TH_BG_BAND,
    color: "rgba(255,255,255,.7)",
    padding: "9px 12px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    minWidth: 150,
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    zIndex: 4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  /* CLIN sits right after Contract ID (left: 150 = ID_W) */
  dtThClin: {
    background: TH_BG_BAND,
    color: "rgba(255,255,255,.7)",
    padding: "9px 12px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    minWidth: 150,
    whiteSpace: "nowrap",
    position: "sticky",
    left: 150,
    zIndex: 4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  /* Remaining Funding / 75% / 100% Expenditure Date — sit between CLIN and the metric-label column */
  dtThRem: {
    background: TH_BG_BAND,
    color: "rgba(255,255,255,.7)",
    padding: "9px 10px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    minWidth: 130,
    lineHeight: 1.3,
    position: "sticky",
    left: 300,
    zIndex: 4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  dtThPct75: {
    background: TH_BG_BAND,
    color: "rgba(255,255,255,.7)",
    padding: "9px 10px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    minWidth: 100,
    lineHeight: 1.3,
    position: "sticky",
    left: 430,
    zIndex: 4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  dtThPct100: {
    background: TH_BG_BAND,
    color: "rgba(255,255,255,.7)",
    padding: "9px 10px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "center",
    border: "none",
    minWidth: 100,
    lineHeight: 1.3,
    position: "sticky",
    left: 530,
    zIndex: 4,
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },
  dtThEmpty: {
    background: TH_BG,
    color: "rgba(255,255,255,.7)",
    padding: "9px 14px",
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    textAlign: "left",
    border: "none",
    minWidth: 140,
    position: "sticky",
    left: 630,
    zIndex: 4,
    boxShadow:
      "inset 0 -2px 0 rgba(255,255,255,.22), 4px 0 8px -2px rgba(0,0,0,0.12)",
  },
  dtThCol: {
    background: TH_BG,
    color: "rgba(255,255,255,.75)",
    padding: "9px 10px",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.06em",
    textAlign: "right",
    border: "none",
    minWidth: 140,
    whiteSpace: "nowrap",
    boxShadow: "inset 0 -2px 0 rgba(255,255,255,.22)",
  },

  /* details data cells */
  dtTdId: {
    padding: "0 12px",
    fontWeight: 700,
    color: "#111827",
    fontSize: 12,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    textAlign: "center",
    background: "#F8FAFC",
    position: "sticky",
    left: 0,
    zIndex: 2,
    borderBottom: "1px solid #E2E8F0",
  },
  dtTdClin: {
    padding: "0 12px",
    fontWeight: 700,
    color: "#111827",
    fontSize: 12,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    textAlign: "center",
    background: "#F8FAFC",
    position: "sticky",
    left: 150,
    zIndex: 2,
    borderBottom: "1px solid #E2E8F0",
  },
  dtTdRem: {
    padding: "0 10px",
    color: "#334155",
    fontSize: 12,
    whiteSpace: "nowrap",
    textAlign: "right",
    background: "#F8FAFC",
    position: "sticky",
    left: 300,
    zIndex: 2,
    fontVariantNumeric: "tabular-nums",
    borderBottom: "1px solid #E2E8F0",
  },
  dtTdPct75: {
    padding: "0 10px",
    color: "#334155",
    fontSize: 12,
    whiteSpace: "nowrap",
    textAlign: "center",
    background: "#F8FAFC",
    position: "sticky",
    left: 430,
    zIndex: 2,
    borderBottom: "1px solid #E2E8F0",
  },
  dtTdPct100: {
    padding: "0 10px",
    color: "#334155",
    fontSize: 12,
    whiteSpace: "nowrap",
    textAlign: "center",
    background: "#F8FAFC",
    position: "sticky",
    left: 530,
    zIndex: 2,
    borderBottom: "1px solid #E2E8F0",
  },
  dtTdLbl: {
    padding: "7px 14px",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    position: "sticky",
    left: 630,
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  dtTdLblAlt: {
    padding: "7px 14px",
    color: "#334155",
    background: "#F7FAFC",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    position: "sticky",
    left: 630,
    zIndex: 2,
    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  },
  dtTdVal: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  dtTdValAlt: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#334155",
    background: "#F7FAFC",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  dtTdPct: {
    padding: "7px 10px",
    textAlign: "right",
    color: "#4F46E5",
    fontWeight: 600,
    background: "#F7FAFC",
    borderBottom: "1px solid #E2E8F0",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main export                                                   */
/* ══════════════════════════════════════════════════════════════ */
export default function FundingExpenditure() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();
  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [downloading, setDownloading] = useState(false);

  /* Per-tab filter state — each tab keeps its own independent draft/applied values */
  const [tabApplied, setTabApplied] = useState({});
  const [tabDraft, setTabDraft] = useState({});
  const defaultFilters = useMemo(() => initFilterValues(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const appliedFilters = tabApplied[activeTab] ?? defaultFilters;
  const draftFilters = tabDraft[activeTab] ?? defaultFilters;

  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const setAppliedFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabApplied((prev) => ({
      ...prev,
      [tab]:
        typeof updater === "function"
          ? updater(prev[tab] ?? initFilterValues())
          : updater,
    }));
  }, []);

  const setDraftFilters = useCallback((updater) => {
    const tab = activeTabRef.current;
    setTabDraft((prev) => ({
      ...prev,
      [tab]:
        typeof updater === "function"
          ? updater(prev[tab] ?? initFilterValues())
          : updater,
    }));
  }, []);

  const [summaryMeta, setSummaryMeta] = useState(null);
  const [detailsMeta, setDetailsMeta] = useState(null);

  const visibleKeys = VISIBLE_FILTER_KEYS;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = (
    visibleKeys
      ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
      : FILTERS_CONFIG
  ).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const actualsThroughLabel = (meta) => {
    // Details' DURATION view (see FUNDING_EXPENDITURE_API.md) has no single
    // actualThroughMonth — it pre-computes its own label from columnMeta instead
    // (or null to hide the badge entirely). Only fall back to the month-number
    // computation when the caller hasn't supplied that field at all.
    if (meta && "actualsLabel" in meta && meta.actualsLabel !== undefined) {
      return meta.actualsLabel;
    }
    const m = Number(meta?.actualThroughMonth);
    const year = new Date().getFullYear();
    if (!m || m < 1 || m > 12) return null;
    return `Actuals through ${MONTH_NAMES[m - 1]} ${year}`;
  };

  const TAB_HEADER = {
    summary: {
      eyebrow: "Financial Reporting",
      title: "Summary",
      accent: "Revenue & Funded Value",
      badge: "Live Forecast",
      date: actualsThroughLabel(summaryMeta),
    },
    details: {
      eyebrow: "Financial Reporting",
      title: "Details",
      accent: "Contract-Level Detail",
      badge: "Live Forecast",
      date: actualsThroughLabel(detailsMeta),
    },
  };

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadFundingExpenditureDetailsAPI(effectiveFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "funding-expenditure-details.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Funding Expenditure download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [effectiveFilters, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Funding Expenditure</span>
        </nav>
        <div className={classes.topActions}>
          {activeTab === "details" && (
            <button
              type="button"
              className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
              title="Download as Excel"
              aria-label="Download Report"
              onClick={handleDownload}
              disabled={downloading}
            >
              <DownloadIcon />
            </button>
          )}
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.push(`/projects/reports/funding_expenditure/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
          activeTab={activeTab}
        />
      )}

      {/* ── Active filter chips (shown when filter bar is hidden) ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            /* Details tab's Project ID filter maps to CLIN in that tab's data */
            const label = f.key === "projectId" && activeTab === "details" ? "CLIN" : f.label;
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${label}: ${val.join(", ")}`
                : `${label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${label}`}
                  disabled={
                    f.key === "year" &&
                    String(val) === String(new Date().getFullYear())
                  }
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Hero header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow={TAB_HEADER[activeTab].eyebrow}
          title={TAB_HEADER[activeTab].title}
          accent={TAB_HEADER[activeTab].accent}
          badge={TAB_HEADER[activeTab].badge}
          date={TAB_HEADER[activeTab].date}
        />
      </div>

      {/* ── Body ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {activeTab === "summary" ? (
            <SummaryTab
              classes={classes}
              filters={effectiveFilters}
              onMeta={setSummaryMeta}
            />
          ) : (
            <DetailsTab
              classes={classes}
              filters={effectiveFilters}
              onMeta={setDetailsMeta}
            />
          )}
        </div>
      </div>
    </div>
  );
}
